import React, { useState } from 'react';
import ModalShell from '../ModalShell/ModalShell';
import './ProfileModal.css';
import { useAuth } from '../../auth/AuthContext';
import type { LocalAuthUser } from '../../data/types';
import { useI18n } from '../../i18n/I18nContext';
import PasswordInput from '../PasswordInput/PasswordInput';

interface ProfileModalProps {
  currentUser: LocalAuthUser;
  onClose: () => void;
  showToast: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

const ProfileModal: React.FC<ProfileModalProps> = ({ currentUser, onClose, showToast }) => {
  const { t } = useI18n();
  const { updateProfile } = useAuth();
  const [email, setEmail] = useState(currentUser.email);
  const [displayName, setDisplayName] = useState(currentUser.displayName);
  const [activeSecurityForm, setActiveSecurityForm] = useState<'password' | 'pin' | null>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    setSaving(true);
    setError(null);

    try {
      if (!displayName.trim()) {
        throw new Error(t('profile.requireDisplayName'));
      }
      if (!email.trim()) {
        throw new Error(t('profile.requireEmail'));
      }
      const changingPassword = activeSecurityForm === 'password' || currentPassword.trim() || newPassword.trim() || confirmPassword.trim();
      const changingPin = activeSecurityForm === 'pin' || newPin.trim() || confirmPin.trim();
      if (changingPassword) {
        if (!currentPassword.trim()) {
          throw new Error(t('profile.requireCurrentPassword'));
        }
      }
      if (changingPassword) {
        if (!newPassword.trim()) {
          throw new Error(t('profile.requireNewPassword'));
        }
        if (newPassword.length < 8) {
          throw new Error(t('profile.minPassword'));
        }
        if (newPassword !== confirmPassword) {
          throw new Error(t('profile.passwordMismatch'));
        }
      }
      if (changingPin) {
        if (!newPin.trim()) {
          throw new Error(t('profile.requireNewPin'));
        }
        if (!/^\d{4,6}$/.test(newPin)) {
          throw new Error(t('profile.invalidPin'));
        }
        if (newPin !== confirmPin) {
          throw new Error(t('profile.pinMismatch'));
        }
      }

      await updateProfile({
        email,
        displayName,
        currentPassword: changingPassword || changingPin ? currentPassword : undefined,
        password: changingPassword ? newPassword : undefined,
        pin: changingPin ? newPin : undefined,
      });
      showToast(t('profile.updated'), 'success');
      onClose();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : t('profile.updateError'));
    } finally {
      setSaving(false);
    }
  }

  function resetPasswordFields() {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  }

  function resetPinFields() {
    setNewPin('');
    setConfirmPin('');
  }

  function toggleSecurityForm(nextForm: 'password' | 'pin') {
    setError(null);
    if (nextForm === activeSecurityForm) {
      setActiveSecurityForm(null);
      if (nextForm === 'password') {
        resetPasswordFields();
      } else {
        resetPinFields();
      }
      return;
    }

    resetPasswordFields();
    resetPinFields();
    setActiveSecurityForm(nextForm);
  }

  return (
    <ModalShell onClose={onClose} ariaLabel={t('profile.title')} className="modal profile-modal">
      <div className="profile-modal-header">
        <h3 style={{ margin: 0 }}>{t('profile.title')}</h3>
        <span className="profile-role-badge">{t(`roles.${currentUser.role}`)}</span>
      </div>

      <div className="profile-form">
        {error && <div className="profile-error">{error}</div>}

        <label>{t('profile.email')}</label>
        <input
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />

        <label>{t('profile.displayName')}</label>
        <input
          type="text"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
        />

        <div className="profile-security-actions">
          <button
            type="button"
            className={`profile-change-password-link${activeSecurityForm === 'password' ? ' active' : ''}`}
            onClick={() => toggleSecurityForm('password')}
          >
            {activeSecurityForm === 'password' ? t('profile.cancelPasswordChange') : t('profile.changePassword')}
          </button>

          <button
            type="button"
            className={`profile-change-password-link${activeSecurityForm === 'pin' ? ' active' : ''}`}
            onClick={() => toggleSecurityForm('pin')}
          >
            {activeSecurityForm === 'pin' ? t('profile.cancelPinChange') : t('profile.changePin')}
          </button>
        </div>

        {activeSecurityForm === 'password' && (
          <div className="profile-password-panel">
            <label>{t('profile.currentPassword')}</label>
            <PasswordInput
              value={currentPassword}
              onChange={setCurrentPassword}
              usernameValue={email}
              autoComplete="current-password"
            />

            <label>{t('profile.newPassword')}</label>
            <PasswordInput
              value={newPassword}
              onChange={setNewPassword}
              usernameValue={email}
              autoComplete="new-password"
            />

            <label>{t('profile.confirmNewPassword')}</label>
            <PasswordInput
              value={confirmPassword}
              onChange={setConfirmPassword}
              usernameValue={email}
              autoComplete="new-password"
            />
          </div>
        )}
        {activeSecurityForm === 'pin' && (
          <div className="profile-password-panel">
            <label>{t('profile.newPin')}</label>
            <PasswordInput
              inputMode="numeric"
              value={newPin}
              maxLength={6}
              onChange={(value) => setNewPin(value.replace(/\D/g, '').slice(0, 6))}
              usernameValue={email}
              autoComplete="off"
            />

            <label>{t('profile.confirmNewPin')}</label>
            <PasswordInput
              inputMode="numeric"
              value={confirmPin}
              maxLength={6}
              onChange={(value) => setConfirmPin(value.replace(/\D/g, '').slice(0, 6))}
              usernameValue={email}
              autoComplete="off"
            />
          </div>
        )}
        <div className="profile-actions">
          <button type="button" className="secondary" onClick={onClose}>
              {t('profile.close')}
            </button>
            <button type="button" className="primary" onClick={handleSave} disabled={saving}>
              {saving ? t('profile.saving') : t('profile.save')}
            </button>
        </div>
      </div>
    </ModalShell>
  );
};

export default ProfileModal;
